import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { 
  User, Building2, FileText, Shield, CheckCircle2, 
  ChevronRight, ChevronLeft, AlertCircle, Loader2, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basic_info', label: 'Información Básica', icon: User },
  { id: 'account_type', label: 'Tipo de Cuenta', icon: Building2 },
  { id: 'details', label: 'Detalles', icon: FileText },
  { id: 'suitability', label: 'Idoneidad', icon: Shield },
  { id: 'documents', label: 'Documentos', icon: Upload },
  { id: 'review', label: 'Revisión', icon: CheckCircle2 },
];

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    phone: '',
    preferred_language: 'es',
    // Account Type
    account_type: '',
    // Personal Details
    date_of_birth: '',
    nationality: 'Mexicana',
    tax_id: '',
    address: '',
    city: '',
    state: '',
    country: 'México',
    postal_code: '',
    // Entity Details
    company_name: '',
    company_tax_id: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_incorporation_date: '',
    company_legal_rep_name: '',
    company_legal_rep_position: '',
    // Suitability
    investment_experience: '',
    risk_tolerance: '',
    investment_horizon: '',
    investment_goals: [],
    source_of_funds: '',
    annual_income_range: '',
    net_worth_range: '',
    is_accredited: false,
    acknowledged_risks: false,
  });

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/onboarding/status');
      setOnboardingStatus(response.data);
      
      // Set current step based on status
      const stepIndex = STEPS.findIndex(s => s.id === response.data.current_step || 
        (response.data.current_step === 'entity_details' && s.id === 'details') ||
        (response.data.current_step === 'personal_details' && s.id === 'details')
      );
      if (stepIndex >= 0) {
        setCurrentStep(stepIndex);
      }
      
      // Pre-fill form with existing data
      const profileResponse = await api.get('/users/profile');
      const profile = profileResponse.data;
      setFormData(prev => ({
        ...prev,
        ...profile,
        investment_goals: profile.investment_goals || [],
      }));
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGoalToggle = (goal) => {
    setFormData(prev => ({
      ...prev,
      investment_goals: prev.investment_goals.includes(goal)
        ? prev.investment_goals.filter(g => g !== goal)
        : [...prev.investment_goals, goal]
    }));
  };

  const saveBasicInfo = async () => {
    setLoading(true);
    try {
      await api.put('/onboarding/basic-info', {
        name: formData.name,
        phone: formData.phone,
        preferred_language: formData.preferred_language,
      });
      toast.success('Información básica guardada');
      setCurrentStep(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const saveAccountType = async () => {
    if (!formData.account_type) {
      toast.error('Seleccione un tipo de cuenta');
      return;
    }
    setLoading(true);
    try {
      await api.put('/onboarding/account-type', {
        account_type: formData.account_type,
      });
      toast.success('Tipo de cuenta guardado');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const saveDetails = async () => {
    setLoading(true);
    try {
      // Save personal details
      await api.put('/onboarding/personal-details', {
        date_of_birth: formData.date_of_birth,
        nationality: formData.nationality,
        tax_id: formData.tax_id,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        postal_code: formData.postal_code,
      });
      
      // If entity, save entity details too
      if (formData.account_type === 'entity') {
        await api.put('/onboarding/entity-details', {
          company_name: formData.company_name,
          company_tax_id: formData.company_tax_id,
          company_address: formData.company_address,
          company_city: formData.company_city,
          company_state: formData.company_state,
          company_incorporation_date: formData.company_incorporation_date,
          company_legal_rep_name: formData.company_legal_rep_name,
          company_legal_rep_position: formData.company_legal_rep_position,
        });
      }
      
      toast.success('Información guardada');
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const saveSuitability = async () => {
    if (!formData.acknowledged_risks) {
      toast.error('Debe reconocer los riesgos de inversión');
      return;
    }
    setLoading(true);
    try {
      await api.put('/onboarding/suitability', {
        investment_experience: formData.investment_experience,
        risk_tolerance: formData.risk_tolerance,
        investment_horizon: formData.investment_horizon,
        investment_goals: formData.investment_goals,
        source_of_funds: formData.source_of_funds,
        annual_income_range: formData.annual_income_range,
        net_worth_range: formData.net_worth_range,
        is_accredited: formData.is_accredited,
        acknowledged_risks: formData.acknowledged_risks,
      });
      toast.success('Cuestionario de idoneidad completado');
      setCurrentStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await api.post('/onboarding/submit-for-review');
      toast.success('Perfil enviado para revisión');
      await checkAuth();
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar. Verifique que todos los campos y documentos estén completos.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepProgress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className={`
                h-10 w-10 rounded-full flex items-center justify-center border-2 transition-colors
                ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : ''}
                ${isCurrent ? 'border-primary text-primary' : ''}
                ${!isCompleted && !isCurrent ? 'border-muted text-muted-foreground' : ''}
              `}>
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                )}
              </div>
              <span className={`text-xs mt-2 text-center hidden md:block ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <Progress value={stepProgress} className="h-1" />
    </div>
  );

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre Completo *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Juan Pérez García"
          required
          data-testid="profile-name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Teléfono *</Label>
        <Input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="+52 55 1234 5678"
          required
          data-testid="profile-phone"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="language">Idioma Preferido</Label>
        <Select
          value={formData.preferred_language}
          onValueChange={(v) => handleInputChange('preferred_language', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="es">Español</SelectItem>
            <SelectItem value="en">English</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderAccountType = () => (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Seleccione el tipo de cuenta que mejor describe su situación de inversión.
      </p>
      <RadioGroup
        value={formData.account_type}
        onValueChange={(v) => handleInputChange('account_type', v)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {[
          { value: 'individual', label: 'Persona Física', desc: 'Inversión a título personal' },
          { value: 'entity', label: 'Persona Moral', desc: 'Empresa o entidad legal' },
          { value: 'family_office', label: 'Family Office', desc: 'Oficina familiar de inversión' },
          { value: 'institutional', label: 'Institucional', desc: 'Fondo o institución financiera' },
        ].map((type) => (
          <Label
            key={type.value}
            htmlFor={type.value}
            className={`
              flex flex-col p-6 border rounded-sm cursor-pointer transition-colors
              ${formData.account_type === type.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
            `}
          >
            <RadioGroupItem value={type.value} id={type.value} className="sr-only" />
            <span className="font-medium mb-1">{type.label}</span>
            <span className="text-sm text-muted-foreground">{type.desc}</span>
          </Label>
        ))}
      </RadioGroup>
    </div>
  );

  const renderDetails = () => (
    <div className="space-y-8">
      {formData.account_type === 'entity' && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium">Información de la Empresa</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Razón Social *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Empresa S.A. de C.V."
              />
            </div>
            <div className="space-y-2">
              <Label>RFC de la Empresa *</Label>
              <Input
                value={formData.company_tax_id}
                onChange={(e) => handleInputChange('company_tax_id', e.target.value)}
                placeholder="XXX010101XXX"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Domicilio Fiscal</Label>
              <Input
                value={formData.company_address}
                onChange={(e) => handleInputChange('company_address', e.target.value)}
                placeholder="Calle, Número, Colonia"
              />
            </div>
            <div className="space-y-2">
              <Label>Representante Legal</Label>
              <Input
                value={formData.company_legal_rep_name}
                onChange={(e) => handleInputChange('company_legal_rep_name', e.target.value)}
                placeholder="Nombre completo"
              />
            </div>
            <div className="space-y-2">
              <Label>Cargo</Label>
              <Input
                value={formData.company_legal_rep_position}
                onChange={(e) => handleInputChange('company_legal_rep_position', e.target.value)}
                placeholder="Director General"
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <h3 className="text-lg font-medium">
          {formData.account_type === 'entity' ? 'Información del Representante' : 'Información Personal'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>RFC Personal</Label>
            <Input
              value={formData.tax_id}
              onChange={(e) => handleInputChange('tax_id', e.target.value)}
              placeholder="XXXX010101XXX"
            />
          </div>
          <div className="space-y-2">
            <Label>Fecha de Nacimiento</Label>
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Nacionalidad</Label>
            <Input
              value={formData.nationality}
              onChange={(e) => handleInputChange('nationality', e.target.value)}
              placeholder="Mexicana"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Dirección *</Label>
            <Input
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Calle, Número, Colonia"
            />
          </div>
          <div className="space-y-2">
            <Label>Ciudad *</Label>
            <Input
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Ciudad de México"
            />
          </div>
          <div className="space-y-2">
            <Label>Estado</Label>
            <Input
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="CDMX"
            />
          </div>
          <div className="space-y-2">
            <Label>País</Label>
            <Input
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              placeholder="México"
            />
          </div>
          <div className="space-y-2">
            <Label>Código Postal</Label>
            <Input
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              placeholder="06600"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSuitability = () => (
    <div className="space-y-8">
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          Esta información nos ayuda a determinar qué oportunidades de inversión son adecuadas para su perfil.
        </AlertDescription>
      </Alert>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Experiencia en Inversiones *</Label>
          <Select
            value={formData.investment_experience}
            onValueChange={(v) => handleInputChange('investment_experience', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sin experiencia previa</SelectItem>
              <SelectItem value="limited">Experiencia limitada (1-3 años)</SelectItem>
              <SelectItem value="moderate">Experiencia moderada (3-10 años)</SelectItem>
              <SelectItem value="extensive">Experiencia extensa (10+ años)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tolerancia al Riesgo *</Label>
          <Select
            value={formData.risk_tolerance}
            onValueChange={(v) => handleInputChange('risk_tolerance', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="conservative">Conservador - Priorizo preservación de capital</SelectItem>
              <SelectItem value="moderate">Moderado - Busco balance entre riesgo y rendimiento</SelectItem>
              <SelectItem value="aggressive">Agresivo - Acepto mayor riesgo por mayor rendimiento potencial</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Horizonte de Inversión *</Label>
          <Select
            value={formData.investment_horizon}
            onValueChange={(v) => handleInputChange('investment_horizon', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="short">Corto plazo (1-3 años)</SelectItem>
              <SelectItem value="medium">Mediano plazo (3-7 años)</SelectItem>
              <SelectItem value="long">Largo plazo (7+ años)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Objetivos de Inversión (seleccione todos los que apliquen)</Label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'income', label: 'Generación de ingresos' },
              { id: 'growth', label: 'Crecimiento de capital' },
              { id: 'preservation', label: 'Preservación de capital' },
              { id: 'diversification', label: 'Diversificación' },
            ].map((goal) => (
              <Label
                key={goal.id}
                className="flex items-center gap-2 p-3 border rounded-sm cursor-pointer hover:bg-muted/50"
              >
                <Checkbox
                  checked={formData.investment_goals.includes(goal.id)}
                  onCheckedChange={() => handleGoalToggle(goal.id)}
                />
                <span className="text-sm">{goal.label}</span>
              </Label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Fuente de Fondos *</Label>
          <Select
            value={formData.source_of_funds}
            onValueChange={(v) => handleInputChange('source_of_funds', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="salary">Salario / Ingresos laborales</SelectItem>
              <SelectItem value="business">Ingresos de negocio propio</SelectItem>
              <SelectItem value="investments">Rendimientos de inversiones</SelectItem>
              <SelectItem value="inheritance">Herencia</SelectItem>
              <SelectItem value="sale_of_assets">Venta de activos</SelectItem>
              <SelectItem value="other">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Rango de Ingreso Anual</Label>
            <Select
              value={formData.annual_income_range}
              onValueChange={(v) => handleInputChange('annual_income_range', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under_500k">Menos de $500,000 MXN</SelectItem>
                <SelectItem value="500k_1m">$500,000 - $1,000,000 MXN</SelectItem>
                <SelectItem value="1m_5m">$1,000,000 - $5,000,000 MXN</SelectItem>
                <SelectItem value="over_5m">Más de $5,000,000 MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Rango de Patrimonio Neto</Label>
            <Select
              value={formData.net_worth_range}
              onValueChange={(v) => handleInputChange('net_worth_range', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under_5m">Menos de $5,000,000 MXN</SelectItem>
                <SelectItem value="5m_20m">$5,000,000 - $20,000,000 MXN</SelectItem>
                <SelectItem value="20m_50m">$20,000,000 - $50,000,000 MXN</SelectItem>
                <SelectItem value="over_50m">Más de $50,000,000 MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 border rounded-sm">
          <Checkbox
            id="accredited"
            checked={formData.is_accredited}
            onCheckedChange={(checked) => handleInputChange('is_accredited', checked)}
          />
          <Label htmlFor="accredited" className="text-sm cursor-pointer">
            Me considero un inversionista acreditado/calificado según las regulaciones aplicables
          </Label>
        </div>

        <div className="p-4 border border-amber-500/30 bg-amber-500/5 rounded-sm space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-amber-500 mb-2">Reconocimiento de Riesgos</p>
              <p>
                La inversión en bienes raíces conlleva riesgos significativos, incluyendo:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Posible pérdida total del capital invertido</li>
                <li>Iliquidez - puede ser difícil vender su participación</li>
                <li>Los rendimientos proyectados no están garantizados</li>
                <li>El valor de los activos puede fluctuar</li>
              </ul>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="risks"
              checked={formData.acknowledged_risks}
              onCheckedChange={(checked) => handleInputChange('acknowledged_risks', checked)}
            />
            <Label htmlFor="risks" className="text-sm cursor-pointer font-medium">
              He leído, entiendo y acepto los riesgos de inversión descritos *
            </Label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <DocumentUploadSection 
      userId={user?.user_id} 
      onComplete={() => setCurrentStep(5)}
      onboardingStatus={onboardingStatus}
    />
  );

  const renderReview = () => (
    <div className="space-y-6">
      {onboardingStatus?.verification_status === 'in_review' ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Su perfil está siendo revisado. Le notificaremos cuando el proceso esté completo.
          </AlertDescription>
        </Alert>
      ) : onboardingStatus?.verification_status === 'approved' ? (
        <Alert className="border-emerald-500/30 bg-emerald-500/5">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <AlertDescription className="text-emerald-500">
            ¡Su cuenta está verificada! Puede comenzar a invertir.
          </AlertDescription>
        </Alert>
      ) : onboardingStatus?.verification_status === 'rejected' || onboardingStatus?.verification_status === 'more_info_required' ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {onboardingStatus?.verification_notes || 'Su solicitud requiere atención. Por favor revise la información o contacte a soporte.'}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            Revise que toda su información esté correcta antes de enviar para aprobación.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="card-dashboard">
          <h3 className="font-medium mb-4">Resumen del Perfil</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Nombre</dt>
              <dd>{formData.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Tipo de Cuenta</dt>
              <dd className="capitalize">{formData.account_type?.replace('_', ' ')}</dd>
            </div>
            {formData.company_name && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Empresa</dt>
                <dd>{formData.company_name}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Ciudad</dt>
              <dd>{formData.city}, {formData.country}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Experiencia</dt>
              <dd className="capitalize">{formData.investment_experience}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Tolerancia al Riesgo</dt>
              <dd className="capitalize">{formData.risk_tolerance}</dd>
            </div>
          </dl>
        </div>

        {onboardingStatus?.verification_status === 'pending' && (
          <Button
            onClick={submitForReview}
            disabled={submitting}
            className="w-full btn-primary"
            data-testid="submit-for-review"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Enviar para Revisión'
            )}
          </Button>
        )}
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderBasicInfo();
      case 1: return renderAccountType();
      case 2: return renderDetails();
      case 3: return renderSuitability();
      case 4: return renderDocuments();
      case 5: return renderReview();
      default: return null;
    }
  };

  const handleNext = () => {
    switch (currentStep) {
      case 0: saveBasicInfo(); break;
      case 1: saveAccountType(); break;
      case 2: saveDetails(); break;
      case 3: saveSuitability(); break;
      case 4: setCurrentStep(5); break;
      default: break;
    }
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 0: return formData.name && formData.phone;
      case 1: return !!formData.account_type;
      case 2: return formData.address && formData.city;
      case 3: return formData.investment_experience && formData.risk_tolerance && formData.acknowledged_risks;
      case 4: return true;
      default: return false;
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="profile-page">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="heading-2">Completar Perfil</h1>
          <Badge variant={
            onboardingStatus?.verification_status === 'approved' ? 'default' :
            onboardingStatus?.verification_status === 'in_review' ? 'secondary' : 'outline'
          }>
            {onboardingStatus?.verification_status === 'approved' ? 'Verificado' :
             onboardingStatus?.verification_status === 'in_review' ? 'En Revisión' :
             onboardingStatus?.verification_status === 'rejected' ? 'Rechazado' :
             onboardingStatus?.verification_status === 'more_info_required' ? 'Info Requerida' : 'Pendiente'}
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Complete su perfil para acceder a oportunidades de inversión.
        </p>
      </div>

      {renderStepIndicator()}

      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle>{STEPS[currentStep].label}</CardTitle>
        </CardHeader>
        <CardContent>
          {renderCurrentStep()}
          
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              Anterior
            </Button>
            
            {currentStep < 5 && (
              <Button
                onClick={handleNext}
                disabled={!canGoNext() || loading}
                className="btn-primary gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Siguiente
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Document Upload Section Component
const DocumentUploadSection = ({ userId, onComplete, onboardingStatus }) => {
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data.documents || []);
      
      if (onboardingStatus?.steps?.documents?.pending_requests) {
        setPendingRequests(onboardingStatus.steps.documents.pending_requests);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleUpload = async (file, category, description) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('description', description);
      formData.append('related_entity_type', 'user');
      formData.append('related_entity_id', userId);
      formData.append('is_confidential', 'true');
      formData.append('access_level', 'admin');

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido correctamente');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const requiredDocs = [
    { category: 'id_document', label: 'Identificación Oficial', description: 'INE, Pasaporte o Cédula Profesional' },
    { category: 'address_proof', label: 'Comprobante de Domicilio', description: 'Reciente (menos de 3 meses)' },
  ];

  const uploadedCategories = new Set(documents.map(d => d.category));

  return (
    <div className="space-y-6">
      <Alert>
        <Upload className="h-4 w-4" />
        <AlertDescription>
          Suba los documentos requeridos para completar su verificación. Formatos aceptados: PDF, JPG, PNG.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {requiredDocs.map((doc) => {
          const isUploaded = uploadedCategories.has(doc.category);
          const uploadedDoc = documents.find(d => d.category === doc.category);
          
          return (
            <div key={doc.category} className="p-4 border rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{doc.label} *</p>
                  <p className="text-sm text-muted-foreground">{doc.description}</p>
                </div>
                {isUploaded ? (
                  <Badge className="bg-emerald-500">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Subido
                  </Badge>
                ) : (
                  <Badge variant="outline">Pendiente</Badge>
                )}
              </div>
              
              {isUploaded && uploadedDoc && (
                <p className="text-sm text-muted-foreground">
                  Archivo: {uploadedDoc.original_filename}
                </p>
              )}
              
              <div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleUpload(e.target.files[0], doc.category, doc.label);
                    }
                  }}
                  className="hidden"
                  id={`upload-${doc.category}`}
                  disabled={uploading}
                />
                <Label
                  htmlFor={`upload-${doc.category}`}
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded-sm cursor-pointer hover:bg-muted transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  {isUploaded ? 'Reemplazar' : 'Subir Archivo'}
                </Label>
              </div>
            </div>
          );
        })}
      </div>

      {pendingRequests.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-amber-500">Documentos Solicitados</h4>
          {pendingRequests.map((req) => (
            <div key={req.request_id} className="p-4 border border-amber-500/30 rounded-sm bg-amber-500/5">
              <p className="font-medium">{req.description}</p>
              <p className="text-sm text-muted-foreground">Categoría: {req.category}</p>
              {req.deadline && (
                <p className="text-sm text-amber-500">Fecha límite: {req.deadline}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Button onClick={onComplete} className="w-full" variant="outline">
        Continuar a Revisión
      </Button>
    </div>
  );
};

export default ProfilePage;
